import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import type { SDUIComponentProps } from '../../renderer/ComponentRenderer';
import { useArcheAction } from '../../hooks/useArcheAction';
import { useArcheContext } from '../../hooks/useArcheContext';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownClasses } from './markdown-classes';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * ChatInterface component - a complete AI chat UI with message history,
 * streaming responses, and auto-scrolling.
 *
 * Integrates with aiInvoke actions for multi-turn conversations.
 * Reads/writes messages to context.input[messagesKey] and triggers
 * the specified sendActionId on send.
 */
export function ChatInterface({ id, props, style }: SDUIComponentProps) {
  const {
    triggerAction,
    updateInput,
    isAIStreaming,
    aiStreamingText,
    aiThinkingText,
    stopAIStreaming,
  } = useArcheAction();
  const { context } = useArcheContext();

  const {
    configId,
    sendActionId,
    messagesKey = 'chatMessages',
    inputKey = 'chatInput',
    outputKey = 'chatResponse',
    placeholder = 'Type a message...',
    welcomeMessage,
    showThinking = true,
  } = props as {
    configId: string;
    sendActionId: string;
    messagesKey?: string;
    inputKey?: string;
    outputKey?: string;
    placeholder?: string;
    welcomeMessage?: string;
    showThinking?: boolean;
  };

  const [typedMessage, setTypedMessage] = useState('');
  const prevIsStreamingRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const messages = (context.input?.[messagesKey] as ChatMessage[]) || [];
  const streamingText = configId ? aiStreamingText[configId] : undefined;
  const thinkingText = configId ? aiThinkingText[configId] : undefined;

  // Auto-scroll when messages change or streaming text updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, streamingText]);

  // Detect streaming completion: when isAIStreaming goes from true→false,
  // read the output and append the assistant message
  useEffect(() => {
    if (prevIsStreamingRef.current && !isAIStreaming) {
      const response = context.output?.[outputKey] as string | undefined;
      if (response) {
        const currentMessages = (context.input?.[messagesKey] as ChatMessage[]) || [];
        updateInput(messagesKey, [...currentMessages, { role: 'assistant', content: response }]);
      }
    }
    prevIsStreamingRef.current = isAIStreaming;
  }, [isAIStreaming, context.output, outputKey, context.input, messagesKey, updateInput]);

  // Auto-size textarea to fit placeholder text on mount
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || typedMessage) return;
    textarea.value = placeholder;
    textarea.style.height = 'auto';
    const needed = Math.min(textarea.scrollHeight, 120);
    textarea.value = '';
    textarea.style.height = `${needed}px`;
  }, [placeholder, typedMessage]);

  const handleSend = () => {
    const content = typedMessage.trim();
    if (!content || isAIStreaming) return;

    // Append user message to messages array
    updateInput(messagesKey, [...messages, { role: 'user', content }]);
    // Set the input key for the aiInvoke action's inputMapping
    updateInput(inputKey, content);
    // Trigger the send action
    triggerAction(sendActionId, {});
    // Clear local input
    setTypedMessage('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleContinue = () => {
    // Append a continuation message and trigger the send action
    updateInput(messagesKey, [
      ...messages,
      { role: 'user', content: 'Please continue from where you left off.' },
    ]);
    updateInput(inputKey, 'Please continue from where you left off.');
    triggerAction(sendActionId, {});
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTypedMessage(e.target.value);
    // Auto-grow textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  return (
    <div
      id={id}
      className="flex flex-col"
      style={style as CSSProperties}
      data-testid="chat-interface"
    >
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-area">
        {/* Welcome message */}
        {messages.length === 0 && welcomeMessage && (
          <div className="flex justify-start">
            <div className={cn('max-w-[80%] rounded-lg px-4 py-2 bg-muted text-foreground')}>
              <div className={markdownClasses}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{welcomeMessage}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {/* Message bubbles */}
        {messages.map((message, index) => (
          <div
            key={index}
            className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')}
          >
            <div
              className={cn(
                'max-w-[80%] rounded-lg px-4 py-2',
                message.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground',
              )}
            >
              {message.role === 'assistant' ? (
                <div className={markdownClasses}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              )}
            </div>
          </div>
        ))}

        {/* Streaming assistant bubble */}
        {isAIStreaming && streamingText && (
          <div className="flex justify-start">
            <div className="max-w-[80%] rounded-lg px-4 py-2 bg-muted text-foreground">
              <div className={markdownClasses}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingText}</ReactMarkdown>
              </div>
              <span
                className="inline-block w-2 h-2 bg-foreground/50 rounded-full animate-pulse ml-1"
                data-testid="streaming-indicator"
              />
            </div>
          </div>
        )}

        {/* Thinking indicator */}
        {isAIStreaming && showThinking && thinkingText && (
          <div className="flex justify-start" data-testid="thinking-indicator">
            <div className="max-w-[80%] rounded-lg px-3 py-2 bg-muted/50 text-muted-foreground text-xs italic">
              Thinking...
            </div>
          </div>
        )}

        {/* Streaming with no text yet — loading dots */}
        {isAIStreaming && !streamingText && !thinkingText && (
          <div className="flex justify-start">
            <div className="rounded-lg px-4 py-2 bg-muted text-foreground">
              <div className="flex gap-1" data-testid="loading-dots">
                <span className="w-2 h-2 bg-foreground/40 rounded-full animate-bounce [animation-delay:0ms]" />
                <span className="w-2 h-2 bg-foreground/40 rounded-full animate-bounce [animation-delay:150ms]" />
                <span className="w-2 h-2 bg-foreground/40 rounded-full animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-border p-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={typedMessage}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isAIStreaming}
            rows={1}
            className={cn(
              'flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground',
              'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
            data-testid="chat-input"
          />
          {isAIStreaming ? (
            <button
              type="button"
              onClick={stopAIStreaming}
              className={cn(
                'flex-shrink-0 rounded-lg px-3 py-2 text-sm font-medium',
                'bg-destructive text-destructive-foreground hover:bg-destructive/90',
              )}
              data-testid="stop-button"
            >
              Stop
            </button>
          ) : messages.length > 0 &&
            messages[messages.length - 1].role === 'assistant' &&
            !typedMessage.trim() ? (
            <button
              type="button"
              onClick={handleContinue}
              className={cn(
                'flex-shrink-0 rounded-lg px-3 py-2 text-sm font-medium',
                'bg-secondary text-secondary-foreground hover:bg-secondary/80',
              )}
              data-testid="continue-button"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSend}
              disabled={!typedMessage.trim()}
              className={cn(
                'flex-shrink-0 rounded-lg px-3 py-2 text-sm font-medium',
                'bg-primary text-primary-foreground hover:bg-primary/90',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
              data-testid="send-button"
            >
              Send
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
